import * as React from 'react'
import { tv, VariantProps } from 'tailwind-variants'

const fileInfoVariants = tv({
  base: 'flex items-center justify-between text-sm text-gray-600',
  variants: {
    size: {
      sm: 'text-xs',
      default: 'text-sm',
      lg: 'text-base'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

const infoItemVariants = tv({
  base: 'flex items-center gap-2',
  variants: {
    size: {
      sm: 'gap-1.5',
      default: 'gap-2',
      lg: 'gap-3'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

const iconVariants = tv({
  base: 'text-gray-500',
  variants: {
    size: {
      sm: 'h-4 w-4',
      default: 'h-5 w-5',
      lg: 'h-6 w-6'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

export interface FileInfoProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof fileInfoVariants> {
  supportedFiles?: string[]
  maxSize?: string
  size?: 'sm' | 'default' | 'lg'
}

const FileInfo = React.forwardRef<HTMLDivElement, FileInfoProps>(
  (
    { className, size = 'default', supportedFiles = ['.docx', '.png', '.jpg', '.pdf'], maxSize = '10MB', ...props },
    ref
  ) => {
    const [showMobileInfo, setShowMobileInfo] = React.useState(false)

    const InfoIcon = () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 11V16M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="12" cy="7.5" r="1" fill="black"/>
      </svg>
    )

    const SizeIcon = () => (
      <svg
        className={iconVariants({ size })}
        fill="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" />
        <path d="M7 7H17V9H7V7Z" />
        <path d="M7 11H17V13H7V11Z" />
        <path d="M7 15H13V17H7V15Z" />
      </svg>
    )

    const handleMobileClick = () => {
      setShowMobileInfo(!showMobileInfo)
    }

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element
        if (showMobileInfo && !target.closest('[data-file-info]')) {
          setShowMobileInfo(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showMobileInfo])

    return (
      <div ref={ref} data-file-info className="relative" {...props}>
        <div className="sm:hidden">
          <button
            onClick={handleMobileClick}
            className="flex items-center justify-center p-2 text-gray-500 hover:text-gray-700 transition-colors border-0 bg-transparent"
            aria-label="Informações sobre arquivos suportados"
          >
            <InfoIcon />
          </button>

          {showMobileInfo && (
            <div className="absolute top-full left-0 z-50 mt-2 w-80 max-w-[90vw] rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <InfoIcon />
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Arquivos Suportados:</p>
                    <p className="text-sm text-gray-600">{supportedFiles.join(', ')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <SizeIcon />
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Tamanho máximo:</p>
                    <p className="text-sm text-gray-600">{maxSize}</p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setShowMobileInfo(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 border-0 bg-transparent"
                aria-label="Fechar"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Desktop: Layout completo */}
        <div className={`hidden sm:flex ${fileInfoVariants({ size, className })}`}>
          <div className={infoItemVariants({ size })}>
            <InfoIcon />
            <span>Arquivos Suportados: {supportedFiles.join(', ')}</span>
          </div>

          <div className={infoItemVariants({ size })}>
            <SizeIcon />
            <span>Tamanho máximo: {maxSize}</span>
          </div>
        </div>
      </div>
    )
  }
)

FileInfo.displayName = 'FileInfo'

export { FileInfo, fileInfoVariants }
